import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Avatar } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

const { Title } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table {
    background: transparent !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-table-tbody > tr > td {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: ${COLORS.hover} !important;
  }
`;

const StaffDetails: React.FC = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [staffData, setStaffData] = useState<any>(null);

  useEffect(() => {
    fetchStaffDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const fetchStaffDetails = async () => {
    if (!staffId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.MERCHANT_GET_STAFF_DETAILS.replace('{staffId}', staffId)
      );
      
      if (response.data.status === 'success') {
        setStaffData(response.data.data);
      } else {
        message.error('Failed to load staff details');
      }
    } catch (error: any) {
      console.error('Error fetching staff details:', error);
      message.error(error.response?.data?.message || 'Failed to load staff details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!staffData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Staff Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/igwena/staff')}>
          Back
        </Button>
        <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
          Staff Details
        </Title>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <Avatar
              src={staffData.portraitUrl ? `data:image/jpeg;base64,${staffData.portraitUrl}` : undefined}
              icon={<UserOutlined />}
              size={64}
              style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
            />
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {staffData.firstName} {staffData.lastName}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                {staffData.email}
              </div>
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Staff ID">{staffData.id}</Descriptions.Item>
          <Descriptions.Item label="Email">{staffData.email}</Descriptions.Item>
          <Descriptions.Item label="First Name">{staffData.firstName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{staffData.lastName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Phone Number">{staffData.phoneNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Country">
            <Space>
              {staffData.countryCode && (
                <ReactCountryFlag
                  countryCode={staffData.countryCode}
                  svg
                  style={{
                    width: '1.5em',
                    height: '1.5em',
                    borderRadius: '2px'
                  }}
                />
              )}
              <span>{staffData.countryCode || '-'}</span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(staffData.status)}</Descriptions.Item>
          <Descriptions.Item label="2FA Enabled">
            {staffData.twoFactorEnabled ? (
              <Tag color="green">Enabled</Tag>
            ) : (
              <Tag color="default">Disabled</Tag>
            )}
          </Descriptions.Item>
          {staffData.role && (
            <Descriptions.Item label="Role">
              <Tag color="blue">{staffData.role}</Tag>
            </Descriptions.Item>
          )}
          {staffData.permissions && staffData.permissions.length > 0 && (
            <Descriptions.Item label="Permissions">
              <Space wrap>
                {staffData.permissions.map((perm: string, index: number) => (
                  <Tag key={index} color="cyan">{perm}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created At">
            {staffData.createdAt ? new Date(staffData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {staffData.updatedAt ? new Date(staffData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>
    </PageContainer>
  );
};

export default StaffDetails;

